import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { track, trackLeadGenerated } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, Send, Building2 } from "lucide-react";

export default function Contact() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.mobile.trim()) || !form.message.trim()) {
      toast.error("Please fill name, valid mobile and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", { ...form, email: form.email || null, attribution: getAttribution() });
      track("form_submit", { form: "contact" });
      trackLeadGenerated(data, {
        intent: "contact",
        lead_type: "contact_form",
        form_name: "Contact Us",
      });
      navigate("/thank-you", { state: { kind: "contact" } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="contact-page">
      <SEOHead title="Contact Us" description="Get in touch with the CollegeUnlock team for admission guidance in Pune." />
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">We're here to help</span>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Contact Us</h1>
          <p className="mt-3 text-slate-600 leading-relaxed">Have a question about admissions or our platform? Send us a message and we'll get back to you.</p>
          <div className="mt-8 space-y-4">
            <Info icon={MapPin} label="Location" value="Pune, Maharashtra, India" />
            <Info icon={Mail} label="Email" value="hello@collegeunlock.in" />
            <Info icon={Phone} label="Phone" value="+91 83789 48584" />
            <Info icon={Building2} label="Legal Entity" value="Spiritual Mantra" testid="contact-legal-entity" />
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm space-y-4">
          <Field label="Name *"><input data-testid="contact-input-name" className="cu-input3" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></Field>
          <Field label="Email"><input data-testid="contact-input-email" type="email" className="cu-input3" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" /></Field>
          <Field label="Mobile *"><input data-testid="contact-input-mobile" className="cu-input3" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" /></Field>
          <Field label="Message *"><textarea data-testid="contact-input-message" rows={4} className="cu-input3 !h-auto py-3" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="How can we help?" /></Field>
          <button data-testid="contact-submit-btn" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B2A6B] px-6 py-3 text-sm font-semibold text-white hover:bg-[#081E4D] disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send Message</>}
          </button>
        </form>
      </div>
      <style>{`.cu-input3{display:block;height:3rem;width:100%;border-radius:0.5rem;border:1px solid #cbd5e1;background:#fff;padding:0 1rem;font-size:0.875rem;outline:none}.cu-input3:focus{box-shadow:0 0 0 2px #0B2A6B;border-color:transparent}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span><div className="mt-1.5">{children}</div></label>;
}
function Info({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B2A6B]/10"><Icon className="h-5 w-5 text-[#0B2A6B]" /></span>
      <div><p className="text-xs text-slate-500">{label}</p><p className="font-medium text-slate-900">{value}</p></div>
    </div>
  );
}
